/* eslint-disable */
import {
    ComponentFactory,
    ComponentFactoryResolver,
    ComponentRef,
    Directive,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChange,
    SimpleChanges,
    Type,
    ViewContainerRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
    selector: '[dynamic-component]',
    standalone: false
})
export class DynamicComponentDirective implements OnDestroy, OnChanges {
    private subscription = new Subject();
    private componentFactory?: ComponentFactory<any>;
    private componentRef?: ComponentRef<any>;
    @Input('dynamic-component') component!: Type<any>;
    @Input() outputs?: any = [];
    @Input() inputs?: any = [];
    @Input() injector?: Injector;

    constructor(
        private viewContainerRef: ViewContainerRef,
        private componentFactoryResolver: ComponentFactoryResolver
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        let componentChanges: Record<string, SimpleChange>;
        const shouldCreateNewComponent =
            changes.component?.previousValue !== changes.component?.currentValue ||
            changes.injector?.previousValue !== changes.injector?.currentValue;

        if (shouldCreateNewComponent) {
            this.destroyComponent();
            this.createComponent();
            componentChanges = this.makeComponentChanges(changes.inputs, true);
        }
        componentChanges = componentChanges || this.makeComponentChanges(changes.inputs, false);
        this.validateOutputs(
            this.componentFactory.outputs,
            this.outputs ?? [],
            this.componentRef.instance
        );
        this.validateInputs(this.componentFactory.inputs, this.inputs ?? []);

        if (changes.inputs) {
            this.bindInputs(
                this.componentFactory.inputs,
                this.inputs ?? {},
                this.componentRef.instance
            );
        }

        if (changes.outputs) {
            this.subscription.next(undefined);
            this.bindOutputs(
                this.componentFactory.outputs,
                this.outputs ?? {},
                this.componentRef.instance
            );
        }

        if ((this.componentRef.instance as OnChanges).ngOnChanges) {
            this.componentRef.instance.ngOnChanges(componentChanges);
        }
    }

    private makeComponentChanges(
        inputsChange: SimpleChange,
        firstChange: boolean
    ): Record<string, SimpleChange> {
        const previousInputs = inputsChange?.previousValue ?? {};
        const currentInputs = inputsChange?.currentValue ?? {};
        return Object.keys(currentInputs).reduce((acc, inputName) => {
            const currentInputValue = currentInputs[inputName];
            const previousInputValue = previousInputs[inputName];
            if (currentInputValue !== previousInputValue) {
                acc[inputName] = new SimpleChange(
                    firstChange ? undefined : previousInputValue,
                    currentInputValue,
                    firstChange
                );
            }
            return acc;
        }, {} as Record<string, SimpleChange>);
    }

    private createComponent(): void {
        this.componentFactory = this.componentFactoryResolver.resolveComponentFactory(
            this.component
        );
        this.componentRef = this.viewContainerRef.createComponent<any>(
            this.componentFactory,
            0,
            this.injector
        );
    }

    private validateOutputs(componentOutputs: any[], userOutputs: any[], componentInstance: any): void {
        componentOutputs.forEach((output) => {
            if (!(componentInstance[output.propName] instanceof EventEmitter)) {
                throw new Error(`Output ${output.propName} must be a typeof EventEmitter`);
            }
        });

        userOutputs.forEach(userOutput => {
            const componentHaveThatOutput = componentOutputs.some(output => output.templateName === userOutput.name);
            if (!componentHaveThatOutput) {
                throw new Error(`Output ${ userOutput.name } is not ${ this.component.name } output.`);
            }
            if (!(userOutput.callbackFn instanceof Function)) {
                throw new Error(`Output ${ userOutput.name } must be a function`);
            }
        });
    }

    private validateInputs(componentInputs: any[], userInputs: any[]): void {
        const userInputsKeys = userInputs.map((input) => input.name);
        userInputsKeys.forEach((userInputKey) => {
            const componentHaveThatInput = componentInputs.some(
                (componentInput) => componentInput.templateName === userInputKey
            );
            if (!componentHaveThatInput) {
                throw new Error(`Input ${userInputKey} is not ${this.component.name} input.`);
            }
        });
    }

    private destroyComponent(): void {
        this.componentRef?.destroy();
        this.viewContainerRef.clear();
    }

    private bindOutputs(componentOutputs: any, userOutputs: any, componentInstance: any): void {
        componentOutputs.forEach((output) => {
            (componentInstance[output.propName] as EventEmitter<any>)
                .pipe(takeUntil(this.subscription))
                .subscribe((event) => {
                    const handler = userOutputs.find(userOutput => userOutput.name === output.templateName)?.callbackFn;
                    if (handler) {
                        handler(event);
                    }
                });
        });
    }

    private bindInputs(componentInputs: any[], userInputs: any[], componentInstance: any): void {
        componentInputs.forEach((input) => {
            const inputValue = userInputs.find(
                (userInput) => userInput.name === input.templateName
            ).value;
            componentInstance[input.propName] = inputValue;
        });
    }

    ngOnDestroy(): void {
        this.destroyComponent();
        this.subscription.next(undefined);
        this.subscription.complete();
    }
}
